/**
 * Close drawer — pick a disposition, fill any required-on-close custom fields,
 * add optional notes. POST /conversations/:id/close via actions.close.
 */
import { useMemo, useState, type JSX } from 'react'
import { CheckCircle2 } from 'lucide-react'
import type { ConversationDetailResponse } from '../../../modules/conversations/types.js'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, inputClass, textareaClass } from './primitives.js'
import type { UseConversationActionsReturn } from './useConversationActions.js'

type Conversation = ConversationDetailResponse['conversation']
type Resolution = 'resolved' | 'merged' | 'cancelled'

const RESOLUTIONS: { key: Resolution; label: string }[] = [
  { key: 'resolved', label: 'Resolved' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'merged', label: 'Merged' },
]

export interface CloseDrawerProps {
  open: boolean
  onClose: () => void
  actions: UseConversationActionsReturn
  conversation: Conversation
}

export default function CloseDrawer({ open, onClose, actions, conversation }: CloseDrawerProps): JSX.Element {
  const requiredFields = useMemo(
    () => conversation.customFields.filter((f) => f.requiredOnClose),
    [conversation.customFields],
  )
  const [resolution, setResolution] = useState<Resolution>('resolved')
  const [notes, setNotes] = useState('')
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(requiredFields.map((f) => [f.key, f.value ?? ''])),
  )

  const missing = requiredFields.some((f) => !(fieldValues[f.key] ?? '').trim())

  const submit = async (): Promise<void> => {
    const customFields =
      requiredFields.length > 0
        ? Object.fromEntries(requiredFields.map((f) => [f.key, fieldValues[f.key] ?? '']))
        : undefined
    const ok = await actions.close(resolution, notes.trim() || undefined, customFields)
    if (ok) onClose()
  }

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Close conversation"
      subtitle="Resolve and archive this conversation"
      icon={<CheckCircle2 className="h-4 w-4" />}
      info={{
        lead: 'Ends this conversation and records how it finished.',
        points: [
          { term: 'Resolved', detail: "The customer's issue was dealt with." },
          { term: 'Cancelled', detail: 'No longer needed — withdrawn, duplicate or raised in error.' },
          { term: 'Merged', detail: 'The work moved to another conversation. Use the Merge action to link them.' },
          { term: 'Required fields', detail: 'Fields your tenant marks required-on-close must be filled before the close is accepted.' },
          { term: 'Parent conversations', detail: 'A parent will not close while any of its children are still open — close those first.' },
        ],
        caution: 'A closed conversation can be reopened, but the closure is recorded in its activity log either way.',
      }}
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void submit()} disabled={missing || actions.busy}>
            {actions.busy ? 'Closing…' : 'Close conversation'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Resolution" required>
          <div className="grid grid-cols-3 gap-2">
            {RESOLUTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setResolution(r.key)}
                className={`rounded-[3px] border px-3 py-2 text-sm font-semibold transition-colors ${
                  resolution === r.key
                    ? 'border-[#3366FF] bg-blue-50 text-[#3366FF]'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </Field>

        {requiredFields.map((f) => (
          <Field key={f.key} label={f.label} required error={!(fieldValues[f.key] ?? '').trim() ? 'Required to close' : undefined}>
            <input
              value={fieldValues[f.key] ?? ''}
              onChange={(e) => setFieldValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.label}
              className={inputClass}
            />
          </Field>
        ))}

        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Resolution summary (optional)…"
            className={textareaClass}
          />
        </Field>
      </div>
    </ActionDrawer>
  )
}
